import React, { useState, useEffect } from 'react';
import {
  DollarSign, Calendar, TrendingUp, TrendingDown, Percent,
  PieChart, Loader, AlertCircle, ChevronDown, ChevronUp,
  Layers, ArrowUpRight, ArrowDownRight, Clock, Award, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const CorporateActions = ({ symbol }) => {
  const [corporateData, setCorporateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSection, setExpandedSection] = useState('dividends');

  useEffect(() => {
    if (symbol) {
      fetchCorporateActions();
    }
  }, [symbol]);

  const fetchCorporateActions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/company/${symbol}/corporate-actions`);
      const data = await response.json();
      
      if (data.success) {
        setCorporateData(data.data);
      } else {
        setError(data.error || 'Failed to load corporate actions');
      }
    } catch (err) {
      console.error('Error fetching corporate actions:', err);
      setError('Failed to load corporate actions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (!num) return 'N/A';
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toFixed(2);
  };

  if (loading) {
    return (
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '2rem',
        textAlign: 'center',
        marginBottom: '1.5rem'
      }}>
        <Loader size={32} style={{ color: '#8b5cf6', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#94a3b8', marginTop: '1rem' }}>Loading corporate actions...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        textAlign: 'center'
      }}>
        <AlertCircle size={24} color="#ef4444" style={{ marginBottom: '0.5rem' }} />
        <p style={{ color: '#f87171', margin: 0, fontSize: '0.9rem' }}>{error}</p>
      </div>
    );
  }

  if (!corporateData) return null;

  const { dividends, splits, shares } = corporateData;

  return (
    <div style={{ marginBottom: '2rem' }}>
      {/* Section Header */}
      <h3 style={{
        fontSize: '1.25rem',
        fontWeight: '700',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: '#e2e8f0'
      }}>
        <Layers size={22} color="#8b5cf6" />
        Corporate Actions & Share Data
      </h3>

      {/* Quick Stats Row - 2 columns on mobile, 4 on desktop */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <style>{`
          @media (min-width: 768px) {
            .corp-stats-grid { grid-template-columns: repeat(4, 1fr) !important; }
          }
        `}</style>
        <QuickStat
          icon={<Percent size={18} />}
          label="Dividend Yield"
          value={dividends?.dividendYield ? `${dividends.dividendYield.toFixed(2)}%` : 'N/A'}
          color="#22c55e"
        />
        <QuickStat
          icon={<Calendar size={18} />}
          label="Ex-Dividend"
          value={formatDate(dividends?.exDividendDate)}
          color="#60a5fa"
        />
        <QuickStat
          icon={<BarChart3 size={18} />}
          label="Shares Outstanding"
          value={formatNumber(shares?.outstanding)}
          color="#8b5cf6"
        />
        <QuickStat
          icon={<TrendingDown size={18} />}
          label="Short Interest"
          value={shares?.shortRatio ? `${shares.shortRatio.toFixed(2)} ratio` : 'N/A'}
          color="#f59e0b"
        />
      </div>

      {/* Expandable Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {/* Dividends Section */}
        <CollapsibleSection
          title="Dividend History"
          icon={<DollarSign size={18} />}
          color="#22c55e"
          isExpanded={expandedSection === 'dividends'}
          onToggle={() => setExpandedSection(expandedSection === 'dividends' ? null : 'dividends')}
          badge={dividends?.history?.length ? `${dividends.history.length} payments` : null}
        >
          {dividends?.history?.length > 0 ? (
            <div>
              {/* Dividend Summary */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '1rem',
                marginBottom: '1rem',
                padding: '1rem',
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '0.5rem'
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Annual Yield</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#22c55e' }}>
                    {dividends.dividendYield ? `${dividends.dividendYield.toFixed(2)}%` : 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Last Payment</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#e2e8f0' }}>
                    ${dividends.history[0]?.amount?.toFixed(2) || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Ex-Dividend Date</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#94a3b8' }}>
                    {formatDate(dividends.exDividendDate)}
                  </div>
                </div>
              </div>

              {/* Dividend Table - Horizontally scrollable on mobile */}
              <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', minWidth: '400px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left', color: '#64748b', fontWeight: '600' }}>Ex-Date</th>
                      <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left', color: '#64748b', fontWeight: '600' }}>Pay Date</th>
                      <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: '#64748b', fontWeight: '600' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dividends.history.map((div, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                        <td style={{ padding: '0.75rem 0.5rem', color: '#e2e8f0' }}>{formatDate(div.exDate)}</td>
                        <td style={{ padding: '0.75rem 0.5rem', color: '#94a3b8' }}>{formatDate(div.payDate)}</td>
                        <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: '#22c55e', fontWeight: '600' }}>
                          ${div.amount?.toFixed(4) || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              No dividend history available for this stock
            </div>
          )}
        </CollapsibleSection>

        {/* Stock Splits Section */}
        <CollapsibleSection
          title="Stock Split History"
          icon={<PieChart size={18} />}
          color="#60a5fa"
          isExpanded={expandedSection === 'splits'}
          onToggle={() => setExpandedSection(expandedSection === 'splits' ? null : 'splits')}
          badge={splits?.history?.length ? `${splits.history.length} splits` : 'No splits'}
        >
          {splits?.history?.length > 0 ? (
            <div>
              {/* Last Split Highlight */}
              {splits.lastSplit && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem',
                  background: 'rgba(96, 165, 250, 0.1)',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Last Split</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#60a5fa' }}>
                      {splits.lastSplit.ratio}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Date</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#e2e8f0' }}>
                      {formatDate(splits.lastSplit.date)}
                    </div>
                  </div>
                </div>
              )}

              {/* Splits Timeline */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {splits.history.map((split, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      background: 'rgba(30, 41, 59, 0.5)',
                      borderRadius: '0.5rem',
                      borderLeft: `3px solid #60a5fa`
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'rgba(96, 165, 250, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <PieChart size={16} color="#60a5fa" />
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#e2e8f0' }}>
                          {split.ratio} Split
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {formatDate(split.date)}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      padding: '0.25rem 0.75rem',
                      background: 'rgba(96, 165, 250, 0.2)',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#60a5fa'
                    }}>
                      {split.ratio}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              No stock splits recorded for this company
            </div>
          )}
        </CollapsibleSection>

        {/* Shares Outstanding Section */}
        <CollapsibleSection
          title="Shares Outstanding"
          icon={<BarChart3 size={18} />}
          color="#8b5cf6"
          isExpanded={expandedSection === 'shares'}
          onToggle={() => setExpandedSection(expandedSection === 'shares' ? null : 'shares')}
          badge={shares?.outstanding ? formatNumber(shares.outstanding) : null}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '1rem'
          }}>
            <ShareMetric
              label="Shares Outstanding"
              value={formatNumber(shares?.outstanding)}
              description="Total shares issued"
              color="#8b5cf6"
            />
            <ShareMetric
              label="Float Shares"
              value={formatNumber(shares?.float)}
              description="Publicly tradable"
              color="#22c55e"
            />
            <ShareMetric
              label="Short Interest"
              value={formatNumber(shares?.shortInterest)}
              description="Shares sold short"
              color="#ef4444"
            />
            <ShareMetric
              label="Short Ratio"
              value={shares?.shortRatio ? `${shares.shortRatio.toFixed(2)} days` : 'N/A'}
              description="Days to cover"
              color="#f59e0b"
            />
          </div>

          {/* Float vs Outstanding Visual */}
          {shares?.outstanding && shares?.float && (
            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>
                Float as % of Outstanding
              </div>
              <div style={{
                background: '#1e293b',
                borderRadius: '0.5rem',
                height: '24px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <div
                  style={{
                    background: 'linear-gradient(90deg, #22c55e, #10b981)',
                    height: '100%',
                    width: `${Math.min((shares.float / shares.outstanding) * 100, 100)}%`,
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingRight: '0.75rem',
                    transition: 'width 0.5s ease'
                  }}
                >
                  <span style={{ fontSize: '0.75rem', fontWeight: '700', color: 'white' }}>
                    {((shares.float / shares.outstanding) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Short Interest Warning */}
          {shares?.shortRatio && shares.shortRatio > 5 && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <AlertCircle size={16} color="#f59e0b" />
              <span style={{ fontSize: '0.85rem', color: '#f59e0b' }}>
                High short interest detected. Short ratio of {shares.shortRatio.toFixed(2)} days indicates significant bearish sentiment.
              </span>
            </div>
          )}
        </CollapsibleSection>
      </div>
    </div>
  );
};

// Helper Components
const QuickStat = ({ icon, label, value, color }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid #334155',
    borderRadius: '0.5rem',
    padding: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  }}>
    <div style={{
      width: '32px',
      height: '32px',
      borderRadius: '0.375rem',
      background: `${color}20`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#e2e8f0' }}>{value}</div>
    </div>
  </div>
);

const CollapsibleSection = ({ title, icon, color, isExpanded, onToggle, badge, children }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid #334155',
    borderRadius: '0.75rem',
    overflow: 'hidden'
  }}>
    <div
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem',
        cursor: 'pointer',
        borderBottom: isExpanded ? '1px solid #334155' : 'none',
        transition: 'background 0.2s ease'
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.05)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '0.5rem',
          background: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color
        }}>
          {icon}
        </div>
        <span style={{ fontWeight: '600', color: '#e2e8f0', fontSize: '0.95rem' }}>{title}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {badge && (
          <span style={{
            padding: '0.25rem 0.75rem',
            background: `${color}20`,
            color,
            borderRadius: '1rem',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            {badge}
          </span>
        )}
        {isExpanded ? (
          <ChevronUp size={20} color="#64748b" />
        ) : (
          <ChevronDown size={20} color="#64748b" />
        )}
      </div>
    </div>
    
    {isExpanded && (
      <div style={{ padding: '1rem' }}>
        {children}
      </div>
    )}
  </div>
);

const ShareMetric = ({ label, value, description, color }) => (
  <div style={{
    background: 'rgba(15, 23, 42, 0.5)',
    borderRadius: '0.5rem',
    padding: '1rem',
    borderTop: `3px solid ${color}`
  }}>
    <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
      {label}
    </div>
    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#e2e8f0', marginBottom: '0.25rem' }}>
      {value}
    </div>
    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
      {description}
    </div>
  </div>
);

export default CorporateActions;
